import loadable from "@loadable/component";
import React, { useEffect, useRef, useState } from "react";

type LazyMountOnceProps = {
  children: React.ReactNode;
  minHeight?: number;
  fallback?: React.ReactNode;
  // Pass-through to IntersectionObserver for how early we mount.
  rootMargin?: string;
};

type LazyLoadOptions = {
  fallback?: React.ReactNode;
  fallbackMinHeight?: number;
} & Omit<NonNullable<Parameters<typeof loadable>[1]>, "fallback">;

type LazyLoadingComponentProps = {
  minHeight?: number;
  label?: string;
};

// Wrap @loadable/component with a shared fallback so code-split components
// don't render blank while their chunk is loading.
export const lazyLoad = <T extends React.ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
  options?: LazyLoadOptions
) => {
  const { fallback, fallbackMinHeight, ...rest } = options || {};
  return loadable(loader, {
    ...rest,
    fallback: fallback ?? <LazyLoadingComponent minHeight={fallbackMinHeight} />,
  });
};

// Visually hidden text for screen readers (used in loading UI).
const visuallyHiddenStyles: React.CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// Default loading UI for lazy-loaded chunks.
export const LazyLoadingComponent = ({ minHeight = 1, label = "Loading" }: LazyLoadingComponentProps) => (
  <div
    style={{
      minHeight,
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
    role="status"
    aria-live="polite"
  >
    <span className="lazy-mount-once__spinner" aria-hidden="true" />
    <span style={visuallyHiddenStyles}>{label}</span>
    <style>
      {`
        @keyframes lazyMountOnceSpin {
          to {
            transform: rotate(360deg);
          }
        }
        .lazy-mount-once__spinner {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 3px solid #d7ddff;
          border-top-color: #3c5dff;
          animation: lazyMountOnceSpin 0.8s linear infinite;
        }
      `}
    </style>
  </div>
);

// Mounts children only after the sentinel enters the viewport.
const LazyMountOnce = ({ children, minHeight = 1, fallback, rootMargin = "0px" }: LazyMountOnceProps) => {
  const [mounted, setMounted] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (mounted) return;
    const node = sentinelRef.current;
    if (!node) return;

    // If IntersectionObserver isn't available, render immediately.
    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }

    let didDisconnect = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setMounted(true);
          observer.disconnect();
          didDisconnect = true;
        }
      },
      { rootMargin }
    );

    observer.observe(node);
    return () => {
      if (!didDisconnect) observer.disconnect();
    };
  }, [mounted]);

  const defaultFallback = <LazyLoadingComponent minHeight={minHeight} />;

  return (
    <div ref={sentinelRef} style={{ minHeight, width: "100%" }} aria-busy={!mounted}>
      {mounted ? children : fallback ?? defaultFallback}
    </div>
  );
};

export default LazyMountOnce;
